import { Directive, Input, HostListener } from '@angular/core';
import { TouchControlsConfig, touchControls } from 'utils/form';
import {
    NgForm,
    NG_VALIDATORS,
    Validator,
    ValidationErrors,
    AbstractControl,
    NgModel,
    ControlContainer,
    NgControl
} from '@angular/forms';

/**
 * Marking all form controls touched and scrolling to first invalid element
 *
 * Used to validate form controls
 *
 * validateOnClick must be referenced to NgForm or FormGroup
 *
 *
 * How To Use:
 *
 * <form #myForm="ngForm"></form> OR <form [formGroup]="myForm" ></form>
 *
 * <button [validateOnClick]="myForm" [validateOnClickConfig]="{scrollToInvalid: false}"></button>
 */
@Directive({ selector: '[validateOnClick]' })
export class ValidateOnClickDirective {
    protected _config: TouchControlsConfig;
    protected _ref: NgForm | NgModel;

    @Input() set validateOnClick(ref: NgForm | NgModel) {
        if (!ref || !(ref instanceof ControlContainer || ref instanceof NgControl)) {
            throw new Error('validateOnClick must be referenced to either NgForm or NgControl');
        } else {
            this._ref = ref;
        }
    }

    @Input() set validateOnClickConfig(config: TouchControlsConfig) {
        if (typeof config !== 'object')
            throw new Error('validateOnClickConfig must be an object of TouchControlsConfig');
        else this._config = config;
    }

    @HostListener('click') onClick() {
        if (this._ref) touchControls(this._ref, this._config);
    }
}

/**
 * mustMatch Form Validator
 * Matches value of current control with another control
 *
 * <input [(ngModel)]="fieldOne" name="fieldOne" />
 * <input [(ngModel)]="fieldTwo" name="fieldTwo" mustMatch="fieldOne" />
 */
@Directive({
    selector: '[mustMatch][ngModel]',
    providers: [{ provide: NG_VALIDATORS, useExisting: MustMatchValidatorDirective, multi: true }]
})
export class MustMatchValidatorDirective implements Validator {
    protected _matchControlName: string;

    @Input() set mustMatch(value: string) {
        if (!value) {
            throw new Error('mustMatch directive must refer to another control name');
        } else if (typeof value !== 'string') {
            throw new Error('mustMatch directive config value must be a string');
        } else {
            this._matchControlName = value;
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const matchControl = control.root.get(this._matchControlName);

        if (!matchControl)
            throw new Error(
                `mustMatch refers to missing NgForm control '${this._matchControlName}'.`
            );

        //Dont show error if another validator has one
        if (matchControl.errors) return null;

        return control.value !== matchControl.value ? { mustMatch: true } : null;
    }
}
